import { CartItem, CartModel } from "../../model/CartModel";
import { AppConstant } from "../../constant/AppConstant";
import { getLocalStorage, setLocalStorage, removeItemLocalStorage } from "../../helper/localStorageHelper";
import { ProductModel } from "../../model/ProductModel";
import { showSuccessAlert } from "../../helper/alertHelper";

export const getCart = (): CartModel => {
  const cart = getLocalStorage(AppConstant.CART_KEY);
  const parsedCart = cart ? JSON.parse(cart) : {};

  return {
    appointment_id: parsedCart.appointment_id ?? "",
    admin_id: parsedCart.admin_id ?? "",
    partner_id: parsedCart.partner_id ?? "",
    created_by_id: parsedCart.created_by_id ?? "",
    is_partner: parsedCart.is_partner ?? false,
    fitting_partner_id: parsedCart.fitting_partner_id ?? "",
    employee_id: parsedCart.employee_id ?? "",
    customer_id: parsedCart.customer_id ?? "",
    payment_id: parsedCart.payment_id ?? "",
    items: Array.isArray(parsedCart.items) ? parsedCart.items : [],
    order_status: 2,
    sub_total: parsedCart.sub_total ?? 0,
    discount_amount: parsedCart.discount_amount ?? 0,
    total_gst_amount: parsedCart.total_gst_amount ?? 0,
    total_price: parsedCart.total_price ?? 0,
    gross_profit: parsedCart.gross_profit ?? 0,
    invoice_date: parsedCart.invoice_date ?? "",
    due_date: parsedCart.due_date ?? "",
    customer_email: parsedCart.customer_email ?? "",
    comment: parsedCart.comment ?? "",
    billing_address: parsedCart.billing_address ?? "",
    fitting_date: parsedCart.fitting_date ?? "",
    fitting_time: parsedCart.fitting_time ?? "",
    is_paid: parsedCart.is_paid,
    document_url: parsedCart.document_url ?? "",
    type: parsedCart.type ?? 1,
    is_sent_to_account: false,
  };
};

export const saveCart = (cart: CartModel): void => {
  setLocalStorage(AppConstant.CART_KEY, JSON.stringify(cart));
  if (window.updateCartCount) {
    window.updateCartCount();
  }
};

export const clearCart = (): void => {
  removeItemLocalStorage(AppConstant.CART_KEY);
  if (window.updateCartCount) {
    window.updateCartCount();
  }
};

export const updateCart = (updatedCart: CartModel): void => {
  saveCart(updatedCart);
};

export const calculateSubTotal = (items: CartItem[]) => {
  return items.reduce((total, item) => {
    if (item.is_tax_included) {
      return total + ((item.total_price ?? 0) - (item.gst_amount ?? 0));
    } else {
      return total + (item.total_price ?? 0);
    }
  }, 0);
};

export const calculateGSTAmount = (items: CartItem[]) => {
  return items.reduce((total, item) => total + (item.gst_amount || 0), 0);
};

export const calculateTotalPrice = (items: CartItem[]) => {
  return calculateSubTotal(items) + calculateGSTAmount(items);
};

export const calculateGrossProfit = (items: CartItem[]) => {
  return items.reduce((total, item) => total + (item.gross_profit || 0), 0);
};

export const addToCart = (product: ProductModel, selectedPrice: number, quantity: number) => {
  const cart = getCart();
  if (!cart.items) {
    cart.items = [];
  }

  const existingItemIndex = cart.items.findIndex((item) => item.item_code === product.item_code);

  if (existingItemIndex >= 0) {
    const existingItem = cart.items[existingItemIndex];
    if (existingItem) {
      existingItem.product_id = product._id;
      existingItem.quantity = (existingItem.quantity || 0) + quantity;
      existingItem.total_price = existingItem.quantity * (existingItem.price || 0);
      existingItem.gross_profit = existingItem.total_price - (existingItem.product_cost * (existingItem.quantity || 0));
    }
  } else {
    cart.items.push({
      product_id: product._id,
      item_code: product.item_code,
      product_title: product.title,
      quantity,
      price: selectedPrice,
      product_cost: product.cost,
      total_price: selectedPrice * quantity,
      gst_percentage: 0,
      gst_amount: 0,
      gross_profit: 0,
    });
  }

  saveCart(cart);
  showSuccessAlert("Product added to your cart successfully!");
};

export const removeToCart = (index: number) => {
  const cart = getCart();
  if (!cart.items) {
    cart.items = [];
  }
  const updatedItems = cart.items.filter((_, i) => i !== index);
  saveCart({ ...cart, items: updatedItems });

  showSuccessAlert("Product remove to your cart successfully!");
  return updatedItems;
};

export const updateCartStateWithCalculations = (cart: CartModel, updatedItems: CartItem[]): CartModel => {
  const subTotal = calculateSubTotal(updatedItems);
  const gstAmount = calculateGSTAmount(updatedItems);
  const totalPrice = calculateTotalPrice(updatedItems);
  const grossProfit = calculateGrossProfit(updatedItems);
  const discountAmount = 0;
  return {
    ...cart,
    items: updatedItems,
    sub_total: subTotal,
    total_gst_amount: gstAmount,
    discount_amount: discountAmount,
    total_price: totalPrice,
    gross_profit: grossProfit,
  };
};